// Shared ET (America/New_York = Florida) calendar-day helpers. Extracted from
// next-deliveries.ts (Step 7.1) so the FedEx schedule module reuses the exact
// same DST-safe, month/year-boundary-safe date arithmetic instead of
// re-implementing it. BEHAVIOR-PRESERVING: next-deliveries.ts imports these and
// is byte-identical in output.
//
// Cutoff convention (locked): 14:00 UTC of the cutoff ET-day. 14:00 UTC reads as
// 10am EDT in summer and 9am EST in winter - both align with the spec, and every
// lane (FL Mon/Wed + FedEx Mon-Fri) shares this one constant so cutoffs stay in
// sync. (Alexis chose lane-consistency over a DST-correct 10am.)

export const ET_TZ = "America/New_York";
export const CUTOFF_UTC_HOUR = 14;
export const MS_PER_DAY = 24 * 60 * 60 * 1000;

export type EtDay = { year: number; month: number; day: number };

export function etDayOf(d: Date): EtDay {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: ET_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return {
    year: parseInt(get("year"), 10),
    month: parseInt(get("month"), 10) - 1,
    day: parseInt(get("day"), 10),
  };
}

// Noon UTC of an ET calendar day is always inside that ET day (regardless of DST),
// so we use it as the anchor for arithmetic + weekday lookup.
export function etDayToAnchor(et: EtDay): Date {
  return new Date(Date.UTC(et.year, et.month, et.day, 12, 0, 0, 0));
}

export function addEtDays(et: EtDay, n: number): EtDay {
  const anchor = etDayToAnchor(et);
  const next = new Date(anchor.getTime() + n * MS_PER_DAY);
  return {
    year: next.getUTCFullYear(),
    month: next.getUTCMonth(),
    day: next.getUTCDate(),
  };
}

export function etWeekday(et: EtDay): number {
  return etDayToAnchor(et).getUTCDay();
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

export function isoFromEtDay(et: EtDay): string {
  return `${et.year}-${pad2(et.month + 1)}-${pad2(et.day)}`;
}

export function etDayFromIso(iso: string): EtDay {
  const [y, m, d] = iso.split("-").map((s) => parseInt(s, 10));
  return { year: y, month: m - 1, day: d };
}

// Cutoff Date for a given cutoff ET-day: 14:00 UTC of that day (the shared
// convention above). Callers compute the cutoff ET-day (delivery minus an offset)
// then pass it here.
export function cutoffAt(cutoffEtDay: EtDay): Date {
  return new Date(
    Date.UTC(cutoffEtDay.year, cutoffEtDay.month, cutoffEtDay.day, CUTOFF_UTC_HOUR, 0, 0, 0),
  );
}
